/**
 * Starter (built-in) email templates for the recruitment workflow.
 *
 * These are display-only definitions shown in the Templates tab so that
 * users have ready-to-use, editable starting points covering:
 *   • Candidate Introduction to Client
 *   • Client Approval Request
 *   • Candidate Shortlisted / Approved follow-up
 *   • Candidate Rejection / Passed
 *   • Interview Invitation
 *   • Follow-up / No Reply nudge
 *   • Offer Release
 *   • Position Closed
 *
 * When a user clicks "Use Template", a copy is saved to their org via
 * `emailTemplateService.upsert` — so they can customise freely without
 * touching the originals.
 */

export interface StarterTemplate {
  id: string;
  category: string;
  name: string;
  subject: string;
  body_html: string;
}

export const STARTER_TEMPLATE_CATEGORIES = [
  'Candidate Introduction',
  'Client Response',
  'Interview',
  'Offer & Closure',
  'Follow-up',
] as const;

export type StarterCategory = (typeof STARTER_TEMPLATE_CATEGORIES)[number];

export const STARTER_TEMPLATES: StarterTemplate[] = [
  // ── Candidate Introduction ────────────────────────────────────────────────
  {
    id: 'starter_intro_general',
    category: 'Candidate Introduction',
    name: 'Candidate Introduction – General',
    subject: 'Opportunity for {{JOB_TITLE}} at {{CLIENT_NAME}}',
    body_html: `Hi {{CLIENT_NAME}},

I hope this message finds you well. I'm reaching out to introduce {{CANDIDATE_NAME}}, a talented professional I believe would be an excellent fit for the {{JOB_TITLE}} role at your organisation.

{{CANDIDATE_NAME}} brings {{CANDIDATE_SUMMARY}} and has a proven track record in {{CANDIDATE_SKILLS}}.

I'd love to get your thoughts on their profile. Please find their resume attached. You can approve, pass, or send a query using the link below.

{{CTA_BUTTON}}

Looking forward to your feedback.

Best regards,
{{RECRUITER_NAME}}
{{ORG_NAME}}`,
  },
  {
    id: 'starter_intro_senior',
    category: 'Candidate Introduction',
    name: 'Candidate Introduction – Senior / Executive',
    subject: 'Senior Candidate: {{CANDIDATE_NAME}} for {{JOB_TITLE}}',
    body_html: `Dear {{CLIENT_NAME}},

I'm pleased to present {{CANDIDATE_NAME}}, a senior-level professional with extensive experience in {{CANDIDATE_SKILLS}}.

Given the scope of the {{JOB_TITLE}} role, I believe {{CANDIDATE_NAME}}'s background aligns strongly with your requirements — particularly their experience at {{CANDIDATE_COMPANY}}.

Please review their profile and let me know your decision:

{{CTA_BUTTON}}

Thank you for your time.

Warm regards,
{{RECRUITER_NAME}}
{{ORG_NAME}}`,
  },

  // ── Client Response ───────────────────────────────────────────────────────
  {
    id: 'starter_shortlisted',
    category: 'Client Response',
    name: 'Candidate Shortlisted – Next Steps',
    subject: 'Great news: {{CANDIDATE_NAME}} has been shortlisted',
    body_html: `Hi {{CLIENT_NAME}},

Thank you for approving {{CANDIDATE_NAME}} for the {{JOB_TITLE}} role. We're excited to move forward!

Here's a quick outline of next steps:
  1. We'll arrange an interview at your earliest convenience.
  2. Our team will coordinate directly with {{CANDIDATE_NAME}} to confirm their availability.
  3. You'll receive a calendar invite once the schedule is confirmed.

Please let us know if you have any preferences for the interview format or specific topics you'd like covered.

Best regards,
{{RECRUITER_NAME}}
{{ORG_NAME}}`,
  },
  {
    id: 'starter_rejection',
    category: 'Client Response',
    name: 'Candidate Not Selected – Passed',
    subject: 'Update on {{CANDIDATE_NAME}} – {{JOB_TITLE}}',
    body_html: `Hi {{CLIENT_NAME}},

Thank you for reviewing {{CANDIDATE_NAME}}'s profile for the {{JOB_TITLE}} position.

We understand this candidate isn't the right fit at this stage. We'll continue our search and present additional profiles that better match your requirements shortly.

If there's any specific feedback or criteria adjustments that would help us refine our search, please feel free to share — we'll incorporate them immediately.

Best regards,
{{RECRUITER_NAME}}
{{ORG_NAME}}`,
  },

  // ── Interview ─────────────────────────────────────────────────────────────
  {
    id: 'starter_interview_invite',
    category: 'Interview',
    name: 'Interview Invitation – Client',
    subject: 'Interview Scheduled: {{CANDIDATE_NAME}} for {{JOB_TITLE}}',
    body_html: `Dear {{CLIENT_NAME}},

Following your approval, we have scheduled an interview with {{CANDIDATE_NAME}} for the {{JOB_TITLE}} role.

Interview Details:
  • Date & Time: {{INTERVIEW_DATE}}
  • Format: {{INTERVIEW_FORMAT}}
  • Location / Link: {{INTERVIEW_LOCATION}}

Please confirm your availability by responding to this email. A calendar invite will be sent separately.

If you need to reschedule, please let us know at least 24 hours in advance.

Best regards,
{{RECRUITER_NAME}}
{{ORG_NAME}}`,
  },
  {
    id: 'starter_interview_candidate',
    category: 'Interview',
    name: 'Interview Invitation – Candidate',
      subject: "You're invited to interview for {{JOB_TITLE}} at {{CLIENT_NAME}}",
    body_html: `Hi {{CANDIDATE_NAME}},

Congratulations! We're pleased to invite you to interview for the {{JOB_TITLE}} position at {{CLIENT_NAME}}.

Interview Details:
  • Date & Time: {{INTERVIEW_DATE}}
  • Format: {{INTERVIEW_FORMAT}}
  • Location / Meeting Link: {{INTERVIEW_LOCATION}}

Please confirm your attendance by replying to this email or clicking the link below:

{{CTA_BUTTON}}

If you have any questions or need to reschedule, don't hesitate to reach out.

Best of luck, and we look forward to speaking with you!

Best regards,
{{RECRUITER_NAME}}
{{ORG_NAME}}`,
  },

  // ── Offer & Closure ───────────────────────────────────────────────────────
  {
    id: 'starter_offer',
    category: 'Offer & Closure',
    name: 'Offer Release – Client Notification',
    subject: 'Offer Extended to {{CANDIDATE_NAME}} – {{JOB_TITLE}}',
    body_html: `Dear {{CLIENT_NAME}},

We're pleased to inform you that an offer has been extended to {{CANDIDATE_NAME}} for the {{JOB_TITLE}} role.

Offer Summary:
  • Position: {{JOB_TITLE}}
  • Offered Compensation: {{OFFER_AMOUNT}}
  • Start Date: {{START_DATE}}

{{CANDIDATE_NAME}} is reviewing the offer and we expect a response by {{OFFER_DEADLINE}}.

We will keep you updated on their decision. Please don't hesitate to reach out if you'd like to discuss any terms.

Best regards,
{{RECRUITER_NAME}}
{{ORG_NAME}}`,
  },
  {
    id: 'starter_position_closed',
    category: 'Offer & Closure',
    name: 'Position Closed – Placement Confirmed',
    subject: '{{JOB_TITLE}} Position Successfully Filled',
    body_html: `Dear {{CLIENT_NAME}},

We're delighted to confirm that the {{JOB_TITLE}} position has been successfully filled by {{CANDIDATE_NAME}}.

Placement Summary:
  • Role: {{JOB_TITLE}}
  • Start Date: {{START_DATE}}
  • Reference ID: {{REF_ID}}

Thank you for your collaboration throughout this process. It has been a pleasure working with your team, and we hope {{CANDIDATE_NAME}} exceeds your expectations.

We look forward to supporting your future hiring needs.

Warm regards,
{{RECRUITER_NAME}}
{{ORG_NAME}}`,
  },

  // ── Follow-up ─────────────────────────────────────────────────────────────
  {
    id: 'starter_followup_client',
    category: 'Follow-up',
    name: 'Follow-up – Awaiting Client Response',
    subject: 'Following up: {{CANDIDATE_NAME}} for {{JOB_TITLE}}',
    body_html: `Hi {{CLIENT_NAME}},

I wanted to follow up on the profile I sent for {{CANDIDATE_NAME}} regarding the {{JOB_TITLE}} role.

We're keen to keep the process moving and ensure you have the best candidates available. If you need any additional information about {{CANDIDATE_NAME}} or would like to arrange a brief call, please let me know.

You can approve, pass, or send a query here:

{{CTA_BUTTON}}

Thank you for your time, and I look forward to hearing from you.

Best regards,
{{RECRUITER_NAME}}
{{ORG_NAME}}`,
  },
];
